import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchEmailConfig,
  saveEmailConfig,
  testEmailConfig,
  fetchEmailLogs,
  sendEmail,
  type UpdateEmailConfigPayload,
  type EmailLogsQuery,
  type SendEmailPayload,
} from '@/services/api/notifications.api';

// ─── Email Config ───────────────────────────────────────────────────────────

export function useEmailConfig(propertyId: string | null) {
  return useQuery({
    queryKey: ['notifications', 'email-config', propertyId],
    queryFn: () => fetchEmailConfig(propertyId as string),
    staleTime: 60_000,
    enabled: propertyId !== null && propertyId !== '',
  });
}

export function useSaveEmailConfig(propertyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateEmailConfigPayload) => saveEmailConfig(propertyId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'email-config'] });
    },
  });
}

export function useTestEmailConfig(propertyId: string) {
  return useMutation({
    mutationFn: () => testEmailConfig(propertyId),
  });
}

// ─── Email Logs ─────────────────────────────────────────────────────────────

export function useEmailLogs(query: EmailLogsQuery) {
  return useQuery({
    queryKey: ['notifications', 'email-logs', query],
    queryFn: () => fetchEmailLogs(query),
    staleTime: 30_000,
  });
}

// ─── Send Email ─────────────────────────────────────────────────────────────

export function useSendEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SendEmailPayload) => sendEmail(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'email-logs'] });
    },
  });
}
